import { useState, useEffect, useRef } from "react";
import { ContractorApi } from "api/contractors/service";
import { format } from "date-fns";
import {
    Calendar as CalendarIcon,
    Plus,
    Trash2,
    FileText,
    AlertCircle,
    IndianRupee,
    X,
    Loader2,
} from "lucide-react";

const cn = (...classes) => classes.filter(Boolean).join(" ");

export function UpgradeWorkOrderDialog({
    open,
    onOpenChange,
    workOrder,
    onUpgradeSuccess,
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState(null);
    const scrollContainerRef = useRef(null);

    const scrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // Form State
    const [endDate, setEndDate] = useState("");
    const [scopeItems, setScopeItems] = useState([]);
    const [scopeNotes, setScopeNotes] = useState("");
    const [milestones, setMilestones] = useState([]);
    const [paymentTerms, setPaymentTerms] = useState("");
    const [termsAndConditions, setTermsAndConditions] = useState("");

    useEffect(() => {
        if (open && workOrder) {
            setEndDate(workOrder.endDate || "");
            setScopeNotes(workOrder.scope_notes || workOrder.scopeNotes || "");

            // Map scope items from workOrder details
            const items = (workOrder.scopeItems || []).map(item => ({
                id: item.id || Math.random().toString(36).substr(2, 9),
                item: item.item || "",
                description: item.description || "",
                quantity: item.quantity || 0,
                unit: item.unit || "Nos",
                unit_price: item.unitCost || item.unit_price || 0,
                total_price: item.totalCost || item.total_price || 0,
                isExisting: true, // Tag as existing
            }));
            setScopeItems(items.length > 0 ? items : [{ id: Date.now().toString(), item: "", description: "", quantity: 0, unit: "Nos", unit_price: 0, total_price: 0, isExisting: false }]);

            // Map milestones if milestone based
            if (workOrder.paymentStructure === "MILESTONE_BASED") {
                const ms = (workOrder.milestones || []).map(m => ({
                    id: m.id || Math.random().toString(36).substr(2, 9),
                    name: m.name || "",
                    description: m.description || "",
                    amount: m.value || m.amount || 0,
                    isExisting: true, // Tag as existing
                }));
                setMilestones(ms.length > 0 ? ms : [{ id: Date.now().toString(), name: "", description: "", amount: 0, isExisting: false }]);
            }

            setPaymentTerms(workOrder.paymentTerms || "");
            setTermsAndConditions(workOrder.terms || "");
        }
    }, [open, workOrder]);

    const grandTotal = scopeItems.reduce((sum, item) => sum + (Number(item.total_price) || 0), 0);
    const totalMilestoneAmount = milestones.reduce(
        (sum, m) => sum + (Number(m.amount) || 0),
        0
    );
    const remainingMilestoneAmount = grandTotal - totalMilestoneAmount;

    const handleScopeItemChange = (id, field, value) => {
        setScopeItems(items =>
            items.map(item => {
                if (item.id === id && !item.isExisting) { // Only allow change for new items
                    const updated = { ...item, [field]: value };
                    if (field === "quantity" || field === "unit_price") {
                        updated.total_price = (Number(updated.quantity) || 0) * (Number(updated.unit_price) || 0);
                    }
                    return updated;
                }
                return item;
            })
        );
    };

    const addScopeItem = () => {
        setScopeItems([
            ...scopeItems,
            { id: Date.now().toString(), item: "", description: "", quantity: 0, unit: "Nos", unit_price: 0, total_price: 0, isExisting: false },
        ]);
    };

    const removeScopeItem = (id) => {
        const itemToRemove = scopeItems.find(item => item.id === id);
        if (itemToRemove && !itemToRemove.isExisting) {
            setScopeItems(scopeItems.filter(item => item.id !== id));
        }
    };

    const handleMilestoneChange = (id, field, value) => {
        setMilestones(ms =>
            ms.map(m => (m.id === id && !m.isExisting ? { ...m, [field]: value } : m))
        );
    };

    const addMilestone = () => {
        setMilestones(prev => {
            const prevTotal = prev.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
            const remaining = Math.max(0, (Number(grandTotal) || 0) - prevTotal);
            return [
                ...prev,
                { id: Date.now().toString(), name: "", description: "", amount: remaining, isExisting: false },
            ];
        });
    };

    const removeMilestone = (id) => {
        const milestoneToRemove = milestones.find(m => m.id === id);
        if (milestoneToRemove && !milestoneToRemove.isExisting) {
            setMilestones(milestones.filter(m => m.id !== id));
        }
    };

    const handleSubmit = async () => {
        if (new Date(endDate) < new Date(workOrder.endDate)) {
            const errorMsg = `End date cannot be earlier than current end date (${format(new Date(workOrder.endDate), "dd MMM yyyy")})`;
            setApiError(errorMsg);
            scrollToTop();
            return;
        }


        setApiError(null);
        setIsSubmitting(true);

        try {
            // Filter only new scope items for the payload
            const newScopeItems = scopeItems.filter(item => !item.isExisting && item.item.trim() !== "");
            if (!newScopeItems || newScopeItems.length === 0) {
                const errorMsg = "At least one scope item is required";
                setApiError(errorMsg);
                scrollToTop();
                return;
            }
            if (newScopeItems.some(item => item.total_price <= 0)) {
                const errorMsg = "Total price must be greater than 0";
                setApiError(errorMsg);
                scrollToTop();
                return;
            }

            // 
            const payload = {
                end_date: endDate,
                payment_terms: paymentTerms,
                terms_and_conditions: termsAndConditions,
                scope_notes: scopeNotes,
                scope_items: newScopeItems.map(({ id, isExisting, ...rest }) => ({
                    ...rest,
                    quantity: Number(rest.quantity),
                    unit_price: Number(rest.unit_price),
                    total_price: Number(rest.total_price),
                })),
            };

            if (workOrder.paymentStructure === "MILESTONE_BASED") {
                // Filter only new milestones for the payload
                const newMilestones = milestones.filter(m => !m.isExisting);

                payload.milestones = newMilestones.map(({ id, isExisting, ...rest }) => ({
                    ...rest,
                    amount: Number(rest.amount),
                }));

                // Calculate total amount from all items and milestones for validation
                if (Math.abs(totalMilestoneAmount - grandTotal) > 0.01) {
                    setApiError(`Total milestone amount (₹${totalMilestoneAmount.toLocaleString()}) must match grand total (₹${grandTotal.toLocaleString()})`);
                    setIsSubmitting(false);
                    scrollToTop();
                    return;
                }
            }

            const response = await ContractorApi.upgradeWorkOrder(workOrder.workorder_id, payload);

            if (response.status) {
                onUpgradeSuccess();
                onOpenChange(false);
            } else {
                setApiError(response.message || "Failed to upgrade work order");
                scrollToTop();
            }
        } catch (err) {
            console.error("Error upgrading work order:", err);
            setApiError("An unexpected error occurred");
            scrollToTop();
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        return `₹${(amount || 0).toLocaleString()}`;
    };

    // UI Helpers
    const Label = ({ children, className = "" }) => (
        <label className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${className}`}>{children}</label>
    );

    const Input = ({ className = "", ...props }) => (
        <input className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${className}`} {...props} />
    );

    const Button = ({ variant = "default", size = "default", className = "", children, icon: Icon, ...props }) => {
        const baseStyles = "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
        const variants = {
            default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
            destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
            outline: "border border-input bg-transparent shadow-sm hover:bg-sm-accent hover:text-sm-accent-foreground",
            secondary: "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
            ghost: "hover:bg-sm-accent hover:text-sm-accent-foreground",
        };
        const sizes = {
            default: "h-9 px-4 py-2",
            sm: "h-8 rounded-md px-3 text-xs",
            icon: "h-9 w-9",
        };
        return (
            <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
                {Icon && <Icon className={cn("h-4 w-4", children ? "mr-2" : "")} />}
                {children}
            </button>
        );
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl border bg-white shadow-2xl animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <div>
                        <h3 className="text-xl font-semibold text-sm-primary">Upgrade Work Order</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Work Order #{workOrder.id} - {workOrder.projectName}
                        </p>
                    </div>
                    <button onClick={() => onOpenChange(false)} className="rounded-full p-2 hover:bg-muted transition-colors">
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Content */}
                <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6 space-y-8">
                    {apiError && (
                        <div className="bg-destructive/10 border border-destructive/20 text-destructive text-sm p-3 rounded-lg flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            {apiError}
                        </div>
                    )}

                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label>End Date</Label>
                            <input
                                type="date"
                                value={endDate}
                                min={workOrder.endDate}
                                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    {/* Scope Items */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Scope Items</h4>
                            <Button variant="ghost" size="sm" onClick={addScopeItem} icon={Plus} className="text-sm-primary">
                                Add Item
                            </Button>
                        </div>

                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 border-b">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium text-xs">Item</th>
                                        <th className="px-4 py-2 text-left font-medium text-xs">Description</th>
                                        <th className="px-4 py-2 text-right font-medium text-xs w-24">Qty</th>
                                        <th className="px-4 py-2 text-left font-medium text-xs w-28">Unit</th>
                                        <th className="px-4 py-2 text-right font-medium text-xs w-32">Unit Price</th>
                                        <th className="px-4 py-2 text-right font-medium text-xs w-32">Total</th>
                                        <th className="px-4 py-2 w-10"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {scopeItems.map((item) => (
                                        <tr key={item.id} className={cn("transition-colors", item.isExisting ? "bg-muted/20" : "hover:bg-muted/30")}>
                                            <td className="p-2">
                                                <input
                                                    value={item.item}
                                                    onChange={(e) => handleScopeItemChange(item.id, "item", e.target.value)}
                                                    placeholder="Item name"
                                                    className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={item.isExisting}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <input
                                                    value={item.description}
                                                    onChange={(e) => handleScopeItemChange(item.id, "description", e.target.value)}
                                                    placeholder="Description"
                                                    className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={item.isExisting}
                                                />
                                            </td>
                                            <td className="p-2 text-right">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => handleScopeItemChange(item.id, "quantity", e.target.value)}
                                                    className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                    disabled={item.isExisting}
                                                />
                                            </td>
                                            <td className="p-2">
                                                <select
                                                    value={item.unit}
                                                    onChange={(e) => handleScopeItemChange(item.id, "unit", e.target.value)}
                                                    className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-xs shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                                                    disabled={item.isExisting}
                                                >
                                                    <option value="Nos">Nos</option>
                                                    <option value="sq_mtrs">Sq. Mtrs</option>
                                                    <option value="sqft">Sqft</option>
                                                    <option value="rft">Rft</option>
                                                    <option value="cum">Cum</option>
                                                </select>
                                            </td>
                                            <td className="p-2 text-right">
                                                <input
                                                    type="number"
                                                    value={item.unit_price}
                                                    onChange={(e) => handleScopeItemChange(item.id, "unit_price", e.target.value)}
                                                    className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                    placeholder="₹0"
                                                    disabled={item.isExisting}
                                                />
                                            </td>
                                            <td className="p-2 text-right font-medium text-xs">
                                                {formatCurrency(item.total_price)}
                                            </td>
                                            <td className="p-2">
                                                {!item.isExisting && (
                                                    <button
                                                        onClick={() => removeScopeItem(item.id)}
                                                        className="text-destructive hover:bg-destructive/10 p-1.5 rounded-md transition-colors"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-muted/20">
                                        <td colSpan={5} className="px-4 py-3 text-right font-semibold">Grand Total:</td>
                                        <td className="px-4 py-3 text-right font-bold text-sm-primary">{formatCurrency(grandTotal)}</td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Scope Notes */}
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                        <p className="text-xs text-muted-foreground mb-1">Scope Notes:</p>
                        <textarea
                            value={scopeNotes}
                            onChange={(e) => setScopeNotes(e.target.value)}
                            placeholder="Enter scope notes..."
                            className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>

                    {/* Milestones */}
                    {workOrder.paymentStructure === "MILESTONE_BASED" && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Milestones</h4>
                                <div className="flex items-center gap-4">
                                    <div className="text-xs text-muted-foreground">
                                        Remaining:&nbsp;
                                        <span className={cn("font-semibold", remainingMilestoneAmount < -0.01 ? "text-destructive" : "text-sm-primary")}>
                                            {formatCurrency(Math.max(0, remainingMilestoneAmount))}
                                        </span>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={addMilestone} icon={Plus} className="text-sm-primary">
                                        Add Milestone
                                    </Button>
                                </div>
                            </div>

                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/50 border-b">
                                        <tr>
                                            <th className="px-4 py-2 text-left font-medium text-xs">Milestone Name</th>
                                            <th className="px-4 py-2 text-left font-medium text-xs">Description</th>
                                            <th className="px-4 py-2 text-right font-medium text-xs w-48">Amount</th>
                                            <th className="px-4 py-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {milestones.map((m) => (
                                            <tr key={m.id} className={cn("transition-colors", m.isExisting ? "bg-muted/20" : "hover:bg-muted/30")}>
                                                <td className="p-2">
                                                    <input
                                                        value={m.name}
                                                        onChange={(e) => handleMilestoneChange(m.id, "name", e.target.value)}
                                                        placeholder="Milestone name"
                                                        className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                        disabled={m.isExisting}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        value={m.description}
                                                        onChange={(e) => handleMilestoneChange(m.id, "description", e.target.value)}
                                                        placeholder="Description"
                                                        className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                        disabled={m.isExisting}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        type="number"
                                                        value={m.amount}
                                                        onChange={(e) => handleMilestoneChange(m.id, "amount", e.target.value)}
                                                        className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                                                        placeholder="₹0"
                                                        min={0}
                                                        disabled={m.isExisting}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    {!m.isExisting && (
                                                        <button
                                                            onClick={() => removeMilestone(m.id)}
                                                            className="text-destructive hover:bg-destructive/10 p-1.5 rounded-md transition-colors"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Terms and Conditions */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <Label>Payment Terms</Label>
                            <textarea
                                value={paymentTerms}
                                onChange={(e) => setPaymentTerms(e.target.value)}
                                placeholder="Enter payment terms..."
                                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Terms & Conditions</Label>
                            <textarea
                                value={termsAndConditions}
                                onChange={(e) => setTermsAndConditions(e.target.value)}
                                placeholder="Enter terms and conditions..."
                                className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t flex justify-end gap-3 bg-muted/10">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="min-w-[120px]"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            "Upgrade Contract"
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
